import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronRight, ChevronLeft, Truck, BadgePercent, Gift, RotateCcw } from 'lucide-react';
import { shopApi, INR } from '../lib/shop';
import { SEGMENTS, THEMES, PALETTE } from '../lib/catalog';
import ShopNavbar from '../components/ShopNavbar';
import ShopFooter from '../components/ShopFooter';
import ProductCard from '../components/ProductCard';
import '../styles/shop.css';

const HERO_IMG = 'https://static.prod-images.emergentagent.com/jobs/ef0791b4-8335-4a62-aebb-5ef8cf8b3a33/images/4cd6c62307a1da1ac224a71af7e7c8b243c728e5c5cc5fcafda7bfc4a99326c5.jpeg';
const HERO_IMG_2 = 'https://static.prod-images.emergentagent.com/jobs/ef0791b4-8335-4a62-aebb-5ef8cf8b3a33/images/96c24bdb3e629c4063cf78a5f017323b2a98200ee9ec474a17bf41133b5eda94.jpeg';

const Hero = () => (
  <section className="bg-[var(--si-navy)] rounded-md overflow-hidden grid md:grid-cols-[1.3fr_1fr]" data-testid="hero">
    <div className="p-7 md:p-12 flex flex-col justify-center si-rise">
      <p className="inline-flex self-start bg-[#E11D48] text-white text-sm md:text-base font-extrabold tracking-[0.2em] px-3 py-1 rounded-sm" data-testid="hero-sale-badge">SALE · SALE · SALE</p>
      <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.05]" data-testid="hero-headline">
        Up to <span className="text-[var(--si-yellow)]">80% OFF</span> on every design
      </h1>
      <p className="mt-4 text-base md:text-lg text-white/90 max-w-lg">Gen-Z tees from {INR(399)} to hand-stitched Banarasi sarees with historical coin motifs. Prices include GST, with no platform or processing fee. You pay only the price you see.</p>
      <div className="mt-7 flex flex-wrap gap-3">
        <Link to="/shop" className="si-btn si-btn-buy !py-3.5 !px-7 text-base" data-testid="hero-cta-shop">Shop the Sale <ArrowRight className="w-4 h-4" /></Link>
        <a href="#price-store" className="si-btn bg-white text-[var(--si-navy)] hover:bg-[#EEF2FB]" data-testid="hero-cta-price">Shop by Price</a>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-1 h-64 md:h-auto md:min-h-[400px]">
      <img src={HERO_IMG_2} alt="Gen-Z slogan tee" className="w-full h-full object-cover" />
      <img src={HERO_IMG} alt="Mughal mohur Banarasi saree" className="w-full h-full object-cover" />
    </div>
  </section>
);

const OFFERS = [
  [Truck, 'Free Delivery', 'All over India on ₹799+', '#E8F5E9', '#138A36'],
  [Gift, 'Buy 1 Get 1 Free', 'On all ₹999 styles', '#FFF4E5', '#C2410C'],
  [Gift, 'Buy 2 Get 1 Free', 'On all ₹3,999 designs', '#FDECEF', '#BE123C'],
  [BadgePercent, 'Zero Extra Charges', 'GST included, no platform or processing fee', '#EEF2FB', '#0B1F4B'],
  [RotateCcw, '7-Day Replacement', 'No questions asked', '#F3E8FF', '#6B21A8'],
];

const OfferStrip = () => (
  <section className="grid grid-cols-2 md:grid-cols-5 gap-2 md:gap-3" data-testid="offer-strip">
    {OFFERS.map(([Icon, t, s, bg, fg]) => (
      <div key={t} className="rounded-md p-3 md:p-4 flex items-center gap-3 border border-black/5" style={{ background: bg }}>
        <Icon className="w-7 h-7 shrink-0" style={{ color: fg }} />
        <div><p className="text-sm md:text-base font-extrabold" style={{ color: fg }}>{t}</p><p className="text-xs md:text-sm text-[var(--si-black)]">{s}</p></div>
      </div>
    ))}
  </section>
);

const PriceStore = () => (
  <section id="price-store" className="si-card p-4 md:p-6 scroll-mt-40" data-testid="price-store">
    <h2 className="text-base md:text-lg font-bold mb-4">Shop by price</h2>
    <div className="grid grid-cols-5 md:grid-cols-10 gap-2 md:gap-3">
      {SEGMENTS.map((s, i) => (
        <a key={s.price} href={`#seg-${s.price}`} data-testid={`price-tile-${s.price}`}
          className="group rounded-md border border-black/5 py-3 text-center hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow]"
          style={{ background: PALETTE[i].bg }}>
          <p className="text-[10px] md:text-xs font-bold text-[var(--si-grey)]">AT</p>
          <p className="text-sm md:text-lg font-extrabold" style={{ color: PALETTE[i].fg }}>{INR(s.price)}</p>
        </a>
      ))}
    </div>
  </section>
);

const Arrow = ({ dir, onClick }) => (
  <button onClick={onClick} aria-label={dir < 0 ? 'Scroll left' : 'Scroll right'} data-testid={`row-arrow-${dir < 0 ? 'left' : 'right'}`}
    className={`hidden md:flex absolute top-1/3 ${dir < 0 ? '-left-3' : '-right-3'} z-10 w-10 h-20 items-center justify-center bg-white shadow-[0_2px_10px_rgba(0,0,0,0.2)] rounded hover:bg-[var(--si-bg-soft)]`}>
    {dir < 0 ? <ChevronLeft className="w-6 h-6" /> : <ChevronRight className="w-6 h-6" />}
  </button>
);

const Row = ({ items }) => {
  const ref = useRef(null);
  const go = (d) => ref.current?.scrollBy({ left: d * ref.current.clientWidth * 0.8, behavior: 'smooth' });
  return (
    <div className="relative">
      <Arrow dir={-1} onClick={() => go(-1)} />
      <div ref={ref} className="flex gap-2 md:gap-3 si-scroll-x pb-1">
        {items.map((p) => <ProductCard key={p.slug} p={p} compact />)}
      </div>
      <Arrow dir={1} onClick={() => go(1)} />
    </div>
  );
};

const SegmentSection = ({ seg, items }) => {
  const c = PALETTE[SEGMENTS.indexOf(seg) % PALETTE.length];
  return (
  <section id={`seg-${seg.price}`} className="si-card p-4 md:p-6 scroll-mt-40" data-testid={`segment-${seg.price}`}
    style={{ backgroundImage: `linear-gradient(180deg, ${c.bg} 0, #fff 140px)` }}>
    <div className="flex items-end justify-between gap-3 mb-4">
      <div className="si-sec-head" style={{ '--si-accent': c.fg }}>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base md:text-lg font-bold">{seg.title}</h2>
          {seg.offer && <span className="bg-[var(--si-green)] text-white text-xs font-bold px-2 py-0.5 rounded-sm">{seg.offer === 'b1g1' ? 'BUY 1 GET 1 FREE' : 'BUY 2 GET 1 FREE'}</span>}
        </div>
        <p className="text-sm text-[var(--si-grey)]">{seg.sub}</p>
      </div>
      <Link to={`/shop?segment=${seg.price}`} className="si-btn si-btn-primary !py-2 !px-4 text-sm shrink-0" data-testid={`segment-view-all-${seg.price}`}>View All</Link>
    </div>
    <Row items={items} />
  </section>
  );
};

const OfferBanner = ({ offer }) => (
  <section className={`rounded-md p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4 ${offer === 'b1g1' ? 'bg-[var(--si-yellow)]' : 'bg-[#E11D48]'}`} data-testid={`offer-banner-${offer}`}>
    <div>
      <p className="text-3xl md:text-5xl font-extrabold text-white leading-none">{offer === 'b1g1' ? 'BUY 1 GET 1 FREE' : 'BUY 2 GET 1 FREE'}</p>
      <p className="mt-2 text-base md:text-lg font-semibold text-white">{offer === 'b1g1' ? 'On every ₹999 kurta, punjabi, saree & tee. Mix and match any two.' : 'On every ₹3,999 historical design. Add any three, pay for two.'}</p>
    </div>
    <Link to={`/shop?segment=${offer === 'b1g1' ? 999 : 3999}`} className="si-btn bg-white text-[var(--si-black)] hover:bg-[#F1F3F6] self-start md:self-auto" data-testid={`offer-banner-cta-${offer}`}>Grab the Offer <ArrowRight className="w-4 h-4" /></Link>
  </section>
);

const ThemeSection = ({ id, items }) => {
  const t = THEMES[id];
  return (
    <section className="si-card overflow-hidden grid md:grid-cols-[280px_1fr]" data-testid={`theme-${id}`}>
      <div className="p-6 md:p-7 flex flex-col justify-center text-white" style={{ background: t.bg }}>
        <p className="text-xs font-bold uppercase tracking-wider text-[var(--si-yellow)]">{t.tag}</p>
        <h2 className="mt-2 text-xl md:text-2xl font-bold leading-tight">{t.title}</h2>
        <p className="mt-2 text-sm text-white/90">{t.copy}</p>
        <Link to={`/shop?theme=${id}`} className="mt-4 si-btn bg-white text-[var(--si-black)] hover:bg-[#F1F3F6] self-start !py-2 !px-4 text-sm" data-testid={`theme-view-all-${id}`}>
          Explore <ChevronRight className="w-4 h-4" />
        </Link>
      </div>
      <div className="p-3 md:p-4 min-w-0"><Row items={items} /></div>
    </section>
  );
};

const ORDER = [399, 599, 799, 'b1g1', 999, 1999, 'independence', 'mughal', 'b2g1', 3999, 5999, 'coins', 'bengal', 7999, 9999, 'temple', 15999];

const Home = () => {
  const [data, setData] = useState({ segments: {}, themes: {} });
  useEffect(() => { shopApi.getHomepage().then((d) => setData({ segments: d.segments || {}, themes: d.themes || {} })); }, []);

  return (
    <div className="si-page">
      <ShopNavbar />
      <main className="max-w-7xl mx-auto px-3 md:px-6 py-4 md:py-6 space-y-4 md:space-y-5">
        <Hero />
        <OfferStrip />
        <PriceStore />
        {ORDER.map((k) => {
          if (k === 'b1g1' || k === 'b2g1') return <OfferBanner key={k} offer={k} />;
          if (typeof k === 'string') return data.themes[k]?.length ? <ThemeSection key={k} id={k} items={data.themes[k]} /> : null;
          const seg = SEGMENTS.find((s) => s.price === k);
          const items = data.segments[String(k)] || [];
          return items.length ? <SegmentSection key={k} seg={seg} items={items} /> : null;
        })}
      </main>
      <ShopFooter />
    </div>
  );
};

export default Home;
